import { z } from 'zod';
import {
  creerEntierOuNull,
  entierOuNull,
  projetObligatoire,
  texteFacultatif,
} from './champs';

/** Durée en secondes, issue de FormulaireSaisieDuree : jamais négative. */
const dureeOuNull = creerEntierOuNull({
  message: 'saisir_une_duree_positive',
  min: 0,
});

/** Nombre de pas de police ou de jours : un compte, donc jamais négatif. */
const compteurOuNull = creerEntierOuNull({
  message: 'saisir_uniquement_des_chiffres',
  min: 0,
});

/**
 * Style de police de la charte graphique.
 *
 * Validé à part du reste, et non comme une clé imbriquée de json_config :
 * flattenError n'indexe que le premier niveau, si bien qu'une erreur sur
 * weight ressortirait sous la clé json_config, loin du champ fautif. Séparé,
 * le message se pose sous l'input concerné.
 *
 * weight et size sont des champs texte que rien ne restreignait : la saisie
 * passait par parseInt à l'envoi, et « gras » devenait NaN, sérialisé en null
 * dans json_config sans que rien ne le signale.
 */
export const schemaStylePolice = z.looseObject({
  family: texteFacultatif,
  style: texteFacultatif,
  weight: compteurOuNull,
  line_height: texteFacultatif,
  size: compteurOuNull,
});

/**
 * Schéma de la charge utile envoyée à la création d'un rapport gabarit.
 *
 * FormulaireRapportGabarit sert à la création comme à la modification et
 * construit une charge utile de 13 clés, plus l'identifiant du gabarit à la
 * modification. z.object retirant les clés qu'il ne déclare pas, chacune est
 * déclarée ici, même sans règle : en oublier une la ferait disparaître de la
 * requête.
 *
 * Le nom et le projet sont exigés. Le reste est facultatif, et les règles se
 * limitent à refuser ce qui n'a pas de sens : des lettres dans un nombre, une
 * durée négative.
 */
export const schemaNouveauRapportGabarit = z.object({
  name: z
    .string({ message: 'saisir_un_nom_de_rapport' })
    .trim()
    .min(1, { message: 'saisir_un_nom_de_rapport' }),

  // Obligatoire : un gabarit sans projet n'est rattaché à rien.
  project_id: projetObligatoire,

  time_zone: texteFacultatif,

  // Vide dans la liste déroulante donne null : aucun groupe n'est destinataire.
  email_group_id: entierOuNull,

  interval_next_generation: dureeOuNull,

  // Dates de dernière et de prochaine génération, en secondes UNIX. Aucune
  // contrainte d'ordre entre les deux : la dernière est passée, la prochaine à
  // venir, et un gabarit jamais lancé n'a ni l'une ni l'autre.
  last_unixtimestamp_run: entierOuNull,
  next_unixtimestamp_run: entierOuNull,

  // Listes déroulantes à deux entrées : pas de liste fermée, qui bloquerait
  // sans message visible un gabarit dont l'API renverrait une valeur que la
  // liste ne propose pas.
  mode: entierOuNull,
  enable: entierOuNull,
  publication_auto: entierOuNull,
  pdf_result: entierOuNull,

  number_of_days: compteurOuNull,

  // Charte graphique, pages de garde et de corps réunies, déjà sérialisées par
  // le formulaire. Le contenu est contrôlé en amont par schemaStylePolice.
  json_config: z.string({ message: 'valeur_invalide' }),
});

/**
 * Schéma de la charge utile envoyée à la modification d'un rapport gabarit.
 * Même contrat, plus l'identifiant du gabarit visé.
 */
export const schemaModificationRapportGabarit =
  schemaNouveauRapportGabarit.extend({
    template_report_id: z.coerce.number({ message: 'valeur_invalide' }).int(),
  });
